/* ----------------- React Native Paper --------------- */
import { Card, Divider, Text } from "react-native-paper";

/* ----------------- Expo --------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";

/* ----------------- Helpers --------------- */
import { getFeedbackTextAndIcon } from "./helpers";

/* ----------------- Translation --------------- */
import { useTranslation } from "react-i18next";

/* ----------------- Components --------------- */
import CardContent from "./CardContent";

/* ----------------- React --------------- */
import { useMemo } from "react";

/* ----------------- Types --------------- */
import type { StatProps } from "../../StatItem";
import type { ScoreRange } from "./types";

/* ----------------- Hooks --------------- */
import { useAppTheme } from "../../../useAppTheme";

/* ----------------- Components --------------- */
import Box from "../../Box";

import type { IconType } from "../../../types";
import { useDevice } from "../../../useDevice";
import Button from "../../Button";
/* ----------------- Components --------------- */
import DonutScore from "../../DonutScore";
import { ScoreRanges } from "./ScoreRanges";

export type StudySessionSummaryCardProps = {
	title: string;
	score: number;
	numPointsEarned?: number;
	numTotalPoints?: number;
	stats: StatProps[];
	numItems: number;
	handleContinue: () => void;
	isSummativeTest?: boolean;
	scoreRanges?: ScoreRange[];
	infoComp: React.ReactNode;
	colorScale?: string[];
	compositeScore?: number;
	maxScore: number;
	showScoreRanges?: boolean;
	isSummative?: boolean;
};

const Feedback = ({ score }: { score: number }) => {
	const theme = useAppTheme();

	const { icon, text } = useMemo(() => getFeedbackTextAndIcon(score), [score]);
	return (
		<Box flexDirection="row" gap={8} alignItems="center">
			<Icon
				color={theme.colors.success.main}
				name={icon as IconType}
				size={24}
			/>
			<Text variant="bodyMedium">{`${text}`}</Text>
		</Box>
	);
};

const Score = ({
	numPointsEarned,
	numTotalPoints,
	score,
	colorScale,
	hasScoreRanges,
	maxScore,
}: {
	numPointsEarned: number;
	numTotalPoints: number;
	score: number;
	colorScale?: string[];
	hasScoreRanges?: boolean;
	maxScore: number;
}) => {
	const theme = useAppTheme();

	const { t } = useTranslation();

	return (
		<Box
			top={-54}
			backgroundColor={theme.colors.elevation.level1}
			position="absolute"
			alignSelf="center"
			width={100}
			height={100}
			borderRadius={100 / 2}
			zIndex={1}
			padding={8}
		>
			<Box
				elevation={0}
				width={84}
				height={84}
				display="flex"
				justifyContent="center"
			>
				<DonutScore
					variant="progress"
					distribution={[score, maxScore - score]}
					radius={52}
					innerRadius={42}
					score={score}
					// colorScale={colorScale}
					textVariant="titleMedium"
					customColorScale={colorScale}
					customChildren={
						<Box justifyContent="center" alignItems="center">
							<Text variant="titleMedium">
								{hasScoreRanges
									? `${score}%`
									: `${numPointsEarned} / ${numTotalPoints}`}
							</Text>
							{!hasScoreRanges && (
								<Text variant="labelSmall">{t("common.points")}</Text>
							)}
						</Box>
					}
				/>
			</Box>
		</Box>
	);
};

const CardHeader = ({
	title,
	score,
	numPointsEarned,
	numTotalPoints,
	handleContinue,
	colorScale,
	compositeScore,
	scoreRanges,
	numItems,
	maxScore,
	showScoreRanges,
	isSummative,
}: Omit<StudySessionSummaryCardProps, "stats" | "infoComp">) => {
	const { isMobile } = useDevice();
	const hasScoreRanges = !!scoreRanges && showScoreRanges;
	const [passScoreRange] = scoreRanges?.slice(-1) || [];
	const tooltip = isSummative
		? `You scored ${score}% on this ${numItems}-item assessment. An ideal target score is ${passScoreRange.range} to maximize your chance of passing.`
		: `You achieved ${score}% mastery on this ${numItems}-item assessment. An ideal mastery percentage is ${passScoreRange.range} to maximize your chance of success.`;

	return (
		<Box
			gap={16}
			flexDirection="column"
			alignItems="center"
			position="relative"
		>
			<Score
				numPointsEarned={numPointsEarned || 0}
				numTotalPoints={numTotalPoints || 0}
				score={hasScoreRanges ? score : compositeScore || 0}
				colorScale={colorScale}
				hasScoreRanges={hasScoreRanges}
				maxScore={maxScore}
			/>
			{scoreRanges && showScoreRanges ? (
				<ScoreRanges scoreRanges={scoreRanges} tooltip={tooltip} />
			) : (
				<Box height={40} />
			)}

			<Box gap={8} alignItems="center">
				<Text
					style={{
						textAlign: "center",
						paddingTop: scoreRanges ? 0 : 58,
					}}
					variant="titleMedium"
				>
					{title}
				</Text>
				<Feedback score={score} />
			</Box>
			{!isMobile && (
				<Button mode="contained" onPress={handleContinue}>
					Continue
				</Button>
			)}
		</Box>
	);
};

const StudySessionSummaryCard = ({
	infoComp,
	title,
	score,
	numPointsEarned,
	numTotalPoints,
	stats,
	numItems,
	handleContinue,
	scoreRanges,
	colorScale,
	compositeScore,
	maxScore,
	showScoreRanges,
	isSummative,
}: StudySessionSummaryCardProps) => {
	return (
		<Card
			style={{
				width: "100%",
			}}
		>
			<Box gap={16}>
				<CardHeader
					title={title}
					handleContinue={handleContinue}
					numItems={numItems}
					score={score}
					maxScore={maxScore}
					scoreRanges={scoreRanges}
					numPointsEarned={numPointsEarned}
					colorScale={colorScale}
					compositeScore={compositeScore}
					numTotalPoints={numTotalPoints}
					showScoreRanges={showScoreRanges}
					isSummative={isSummative}
				/>
				<Divider />
			</Box>
			<CardContent stats={stats} />
			{infoComp}
		</Card>
	);
};

export default StudySessionSummaryCard;
